import type { VehicleInput } from '@/hooks/useAdminVehicles';
import type { AdminVehicle, AssetClass } from '@/lib/types';

/** What the unit form is made of, so the dialog is layout rather than a list. */

interface Field {
  key: keyof VehicleInput & string;
  label: string;
  placeholder: string;
}

export const TEXT_FIELDS: readonly Field[] = [
  { key: 'name', label: 'Name', placeholder: 'Truck 14' },
  { key: 'plateNumber', label: 'Plate number', placeholder: 'ABC 123' },
  { key: 'brand', label: 'Brand', placeholder: 'Volvo' },
  { key: 'model', label: 'Model', placeholder: 'FH16' },
  { key: 'color', label: 'Colour', placeholder: 'White' },
  { key: 'vin', label: 'VIN', placeholder: '17 characters' },
];

export const NUMBER_FIELDS: readonly Field[] = [
  { key: 'year', label: 'Year', placeholder: '2024' },
  { key: 'fuelTankVolume', label: 'Tank (litres)', placeholder: '600' },
  { key: 'initialMileage', label: 'Initial mileage (km)', placeholder: '120000' },
  { key: 'speedLimit', label: 'Speed limit (km/h)', placeholder: '90' },
];

export type UnitFormState = Record<string, string> & { assetClass: AssetClass };

export function stateFrom(vehicle: AdminVehicle | null): UnitFormState {
  const assetClass = vehicle?.assetClass ?? 'POWERED';
  if (!vehicle) return { assetClass };

  // Built as a plain record and merged once, so `assetClass` keeps its union
  // type instead of being widened to a string by a keyed write.
  const values: Record<string, string> = {
    ident: vehicle.ident ?? '',
    groupId: vehicle.groupId ?? '',
    driverId: vehicle.driverId ?? '',
  };
  for (const { key } of [...TEXT_FIELDS, ...NUMBER_FIELDS]) {
    const value = vehicle[key as keyof AdminVehicle];
    values[key] = value === null || value === undefined ? '' : String(value);
  }

  return { ...values, assetClass };
}

/** An empty field means "no value", which for a nullable column is null. */
export const text = (value: string | undefined) =>
  !value || value.trim() === '' ? null : value.trim();

export const number = (value: string | undefined) =>
  !value || value.trim() === '' ? null : Number(value);
